/**
 * Enriched list cards — ownership, availability and price computed once for the whole app.
 *
 * Mirrors `enrichedDeckCardsAtom` (features/decks/state/allDeckCards.ts) with one deliberate
 * difference: rows whose full card is not yet cached are **kept** (with null price/image) so the
 * list views can render skeleton cells instead of dropping cards.
 *
 * Ownership reads only the pre-aggregated availability atoms. Never call `useCardAvailability`
 * per row — it rescans every printing and every deck card on each invocation.
 */
import { atom } from 'jotai';

import { collectionOwnedByScryfallIdAtom } from '@/features/collection/state/collectionAtom';
import { DEFAULT_USER_SETTINGS, settingsStateAtom } from '@/features/settings/state/settingsAtom';
import { getDisplayImageUris } from '@/lib/cardUtils';
import { getSimplePriceFromPrices } from '@/lib/price';
import { cardIndexByIdAtom } from '@/services/cardDatabase';
import { collectionOwnedByNameAtom, deckUsageByNameAtom } from '@/state/cardAvailability';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { EnrichedListCard } from '@/types/lists';

import { listCardsStateAtom } from './listsAtom';

/**
 * All list cards across all lists, enriched with ownership, availability and price.
 */
export const enrichedListCardsAtom = atom<EnrichedListCard[]>((get) => {
  const { data } = get(listCardsStateAtom);
  const fullCardCache = get(fullCardCacheAtom);
  const cardIndexById = get(cardIndexByIdAtom);
  const ownedByScryfallId = get(collectionOwnedByScryfallIdAtom);
  const ownedByNameLower = get(collectionOwnedByNameAtom);
  const usageByName = get(deckUsageByNameAtom);
  const currency = get(settingsStateAtom).data?.currency ?? DEFAULT_USER_SETTINGS.currency;

  const enriched: EnrichedListCard[] = [];

  for (const listCard of data) {
    const fullCard = fullCardCache.get(listCard.scryfall_id);
    const nameLower = fullCard?.name_lower ?? listCard.name.toLowerCase();

    const ownedThisPrinting = ownedByScryfallId.get(listCard.scryfall_id) ?? 0;
    const ownedByName = ownedByNameLower.get(nameLower) ?? 0;

    // Only copies committed to active decks consume availability — brewing decks,
    // maybeboards and archived decks never reserve a physical copy.
    let committed = 0;
    const committedByDeck = usageByName.get(nameLower)?.committed;
    if (committedByDeck) {
      for (const qty of committedByDeck.values()) {
        committed += qty;
      }
    }

    const unitPrice = getSimplePriceFromPrices(fullCard?.prices, currency);
    const imageUris = fullCard ? getDisplayImageUris(fullCard) : cardIndexById.get(listCard.scryfall_id)?.image_uris;

    enriched.push({
      ...listCard,
      owned_quantity: ownedThisPrinting,
      owned_other_editions: Math.max(0, ownedByName - ownedThisPrinting),
      available_quantity: ownedByName - committed,
      unit_price: unitPrice,
      total_price: unitPrice != null ? unitPrice * listCard.quantity : null,
      sample_image_uris: imageUris?.small && imageUris.normal ? { small: imageUris.small, normal: imageUris.normal } : null,
    });
  }

  return enriched;
});

/** Enriched list cards grouped by list id. */
export const enrichedListCardsByListAtom = atom((get) => {
  const byList = new Map<string, EnrichedListCard[]>();

  for (const card of get(enrichedListCardsAtom)) {
    const existing = byList.get(card.list_id);
    if (existing) {
      existing.push(card);
    } else {
      byList.set(card.list_id, [card]);
    }
  }

  return byList;
});

export interface ListStats {
  /** Distinct entries, matching what the list detail header counts. */
  cardCount: number;
  /** Sum of `quantity` across entries. */
  totalCopies: number;
  /** Sum of `unit_price × quantity` over entries that have a price. */
  totalPrice: number;
  /** Entries whose printing has no cached price, so they are excluded from `totalPrice`. */
  unpricedCount: number;
  /** Entries the user owns at least one copy of, in any printing. */
  ownedCount: number;
}

/**
 * Live per-list stats. The denormalized `card_count` / `total_value` on the list document are
 * never written by the client, so they are only a stale fallback.
 */
export const listStatsByListAtom = atom((get) => {
  const statsByList = new Map<string, ListStats>();

  for (const [listId, cards] of get(enrichedListCardsByListAtom)) {
    const stats: ListStats = { cardCount: cards.length, totalCopies: 0, totalPrice: 0, unpricedCount: 0, ownedCount: 0 };

    for (const card of cards) {
      stats.totalCopies += card.quantity;
      if (card.total_price == null) {
        stats.unpricedCount++;
      } else {
        stats.totalPrice += card.total_price;
      }
      if (card.owned_quantity + card.owned_other_editions > 0) stats.ownedCount++;
    }

    statsByList.set(listId, stats);
  }

  return statsByList;
});
